import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api';

// ── Workflow Architecture friendly-label maps ─────────────────────────────────
const PRIMITIVE_FRIENDLY_LABEL = {
  goal_interpretation:   'Understand Request',
  capability_reasoning:  'Check Capabilities',
  task_decomposition:    'Break Into Steps',
  proposal_generation:   'Create Execution Plan',
  risk_review:           'Critique the Plan',
  execution_instruction: 'Write Instructions',
  verification:          'Check the Result',
  repair_planning:       'Plan a Fix',
  result_summarization:  'Summarize the Work',
  research:              'Pre-Planning Research',
};

const REASONING_FRIENDLY_LABEL = {
  single_worker:            'One AI handles this step',
  adversarial_review:       "One AI critiques another's step",
  multi_model_deliberation: 'Multiple AIs reason together',
  consensus:                'AIs build a shared answer',
  verification_only:        'Checks the outcome',
};

const MODEL_FRIENDLY = { claude: 'Claude', chatgpt: 'ChatGPT', gemini: 'Gemini' };

// ── Goal Check panel ──────────────────────────────────────────────────────────
function GoalCheckPanel() {
  const [input, setInput]     = useState('');
  const [mode, setMode]       = useState('text');  // 'text' | 'goal'
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState(null);

  async function runCheck() {
    if (!input.trim()) return;
    setLoading(true); setErr(null); setResult(null);
    try {
      const body = mode === 'goal' ? { goal: input.trim() } : { text: input.trim() };
      const res  = await apiFetch('/api/goal-check', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setResult(data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  const blocked = result?.blocked;

  return (
    <div className="goalcheck">
      <div className="goalcheck-title">Goal → Blocker → Unblocker Check</div>
      <div className="goalcheck-row">
        <select
          className="goalcheck-mode"
          value={mode}
          onChange={e => setMode(e.target.value)}
        >
          <option value="text">Free text</option>
          <option value="goal">Capability name</option>
        </select>
        <input
          className="goalcheck-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={mode === 'goal' ? 'e.g. build_website' : 'e.g. Build my business website'}
          onKeyDown={e => e.key === 'Enter' && runCheck()}
        />
        <button
          className="goalcheck-btn"
          onClick={runCheck}
          disabled={loading || !input.trim()}
        >{loading ? 'Checking…' : 'Check'}</button>
      </div>

      {err && <p className="goalcheck-error">{err}</p>}

      {result && !result.matched && (
        <div className="goalcheck-nomatch">
          <span>No goal matched.</span>
          {result.knownGoals && (
            <span className="goalcheck-known"> Known: {result.knownGoals.join(', ')}</span>
          )}
        </div>
      )}

      {result?.matched && (
        <div className={`goalcheck-result goalcheck-result--${blocked ? 'blocked' : 'ok'}`}>
          {/* Header */}
          <div className="goalcheck-result-header">
            <span className={`goalcheck-badge goalcheck-badge--${blocked ? 'blocked' : 'ok'}`}>
              {blocked ? 'BLOCKED' : 'AVAILABLE'}
            </span>
            <span className="goalcheck-goal">{result.goal}</span>
          </div>

          {result.inferredFrom && (
            <div className="goalcheck-meta">{result.inferredFrom}</div>
          )}

          {blocked && (
            <>
              <div className="goalcheck-row-item">
                <span className="goalcheck-label">Nearest blocker</span>
                <code className="goalcheck-val">{result.nearestBlocker}</code>
              </div>
              <div className="goalcheck-row-item">
                <span className="goalcheck-label">Missing</span>
                <span className="goalcheck-val goalcheck-missing">{result.missingPrerequisite}</span>
              </div>
              <div className="goalcheck-row-item">
                <span className="goalcheck-label">Recommended action</span>
                <span className="goalcheck-val goalcheck-action">{result.recommendedNextAction}</span>
              </div>
              <div className="goalcheck-row-item">
                <span className="goalcheck-label">Dependency chain</span>
                <code className="goalcheck-chain">{result.dependencyChain}</code>
              </div>
              {result.followUpSequence?.length > 0 && (
                <div className="goalcheck-followup">
                  <div className="goalcheck-followup-title">Follow-up sequence</div>
                  <ol className="goalcheck-followup-list">
                    {result.followUpSequence.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}
            </>
          )}

          {!blocked && (
            <div className="goalcheck-ok-msg">All prerequisites satisfied — goal is actionable.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Capability Check sub-panel ────────────────────────────────────────────────
function CapabilityCheckForm() {
  const [capName, setCapName]   = useState('');
  const [factsStr, setFactsStr] = useState('');
  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState(null);

  async function runCheck() {
    if (!capName.trim()) return;
    setLoading(true);
    setErr(null);
    setResult(null);
    try {
      let facts = {};
      if (factsStr.trim()) {
        try { facts = JSON.parse(factsStr); }
        catch (_) { throw new Error('Facts must be valid JSON'); }
      }
      const res = await apiFetch('/api/system-brain/capability-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ capability: capName.trim(), facts }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setResult(data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="capgraph-check">
      <div className="capgraph-check-title">Capability Blocker Check</div>
      <div className="capgraph-check-row">
        <input
          className="capgraph-check-input"
          value={capName}
          onChange={e => setCapName(e.target.value)}
          placeholder="capability name (e.g. build_website)"
          onKeyDown={e => e.key === 'Enter' && runCheck()}
        />
        <button
          className="capgraph-check-btn"
          onClick={runCheck}
          disabled={loading || !capName.trim()}
        >{loading ? 'Checking…' : 'Check'}</button>
      </div>
      <textarea
        className="capgraph-check-facts"
        value={factsStr}
        onChange={e => setFactsStr(e.target.value)}
        placeholder={'Optional fact overrides (JSON):\n{ "wordpress_auth": true }'}
        rows={3}
      />
      {err && <p className="capgraph-check-error">{err}</p>}
      {result && (
        <div className={`capgraph-result capgraph-result--${result.blocked ? 'blocked' : 'ok'}`}>
          <div className="capgraph-result-status">
            {result.blocked ? '⛔ Blocked' : '✓ Available'}
            <span className="capgraph-result-cap">{result.capability}</span>
          </div>
          {result.missing?.length > 0 && (
            <div className="capgraph-result-row">
              <span className="capgraph-result-label">Missing:</span>
              <span className="capgraph-result-val">{result.missing.join(', ')}</span>
            </div>
          )}
          {result.chains?.length > 0 && (
            <div className="capgraph-result-row">
              <span className="capgraph-result-label">Chain{result.chains.length > 1 ? 's' : ''}:</span>
              <div className="capgraph-result-chains">
                {result.chains.map((c, i) => <code key={i} className="capgraph-chain">{c}</code>)}
              </div>
            </div>
          )}
          <div className="capgraph-result-explanation">{result.explanation}</div>
        </div>
      )}
    </div>
  );
}

// ── Graph node card ───────────────────────────────────────────────────────────
function GraphNode({ name, node }) {
  const [open, setOpen] = useState(false);
  const satisfied = node._satisfied;

  return (
    <div className={`capgraph-node capgraph-node--${satisfied ? 'ok' : 'blocked'}`}>
      <button className="capgraph-node-header" onClick={() => setOpen(o => !o)}>
        <span className="capgraph-node-toggle">{open ? '▾' : '▸'}</span>
        <span className={`capgraph-node-dot capgraph-node-dot--${satisfied ? 'ok' : 'blocked'}`} />
        <span className="capgraph-node-name">{name}</span>
        {node.handler && <span className="capgraph-node-handler">→ {node.handler}</span>}
        <span className={`capgraph-node-status ${satisfied ? 'capgraph-ok' : 'capgraph-blocked'}`}>
          {satisfied ? 'available' : 'blocked'}
        </span>
      </button>
      {open && (
        <div className="capgraph-node-body">
          <p className="capgraph-node-desc">{node.description}</p>
          {node.requires?.length > 0 && (
            <div className="capgraph-node-requires">
              <span className="capgraph-node-requires-label">Requires:</span>
              {node.requires.map(r => (
                <code key={r} className="capgraph-node-req">{r}</code>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Workflow Memory tab ───────────────────────────────────────────────────────
const MEMORY_TYPE_LABELS = {
  repair_strategy:     'Repair Strategies',
  planning_pattern:    'Planning Patterns',
  verification_lesson: 'Verification Lessons',
  research_finding:    'Research Findings',
  workflow_preference: 'Workflow Preferences',
};
const ALL_MEMORY_TYPES = Object.keys(MEMORY_TYPE_LABELS);
const ALL_SCOPES       = ['global', 'project', 'conversation'];

function parseMemoryValue(raw) {
  if (!raw) return { summary: '', full: '' };
  try {
    const parsed = typeof raw === 'object' ? raw : JSON.parse(raw);
    if (typeof parsed === 'string') return { summary: parsed, full: parsed };
    const summary = parsed.lesson || parsed.summary || parsed.text || JSON.stringify(parsed);
    return { summary, full: JSON.stringify(parsed, null, 2) };
  } catch (_) {
    const s = String(raw);
    return { summary: s, full: s };
  }
}

function fmtDate(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString(); } catch (_) { return ts; }
}

function WorkflowMemoryPanel() {
  const [entries,     setEntries]     = useState(null);
  const [typeFilter,  setTypeFilter]  = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [expanded,    setExpanded]    = useState(new Set());
  const [confirming,  setConfirming]  = useState(new Set()); // ids awaiting delete confirm
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);

  const fetchMemory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (typeFilter  !== 'all') params.set('type',  typeFilter);
      if (scopeFilter !== 'all') params.set('scope', scopeFilter);
      const res  = await apiFetch(`/api/memory?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setEntries(data.entries || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, scopeFilter]);

  useEffect(() => { fetchMemory(); }, [fetchMemory]);

  function toggleExpanded(id) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function requestDelete(id) {
    setConfirming(prev => new Set(prev).add(id));
  }

  function cancelDelete(id) {
    setConfirming(prev => { const n = new Set(prev); n.delete(id); return n; });
  }

  async function confirmDelete(id) {
    try {
      const res = await apiFetch(`/api/memory/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.error || `Delete failed (HTTP ${res.status})`);
        return;
      }
      // Remove from local state — no full re-fetch needed
      setEntries(prev => prev.filter(e => e.id !== id));
      setConfirming(prev => { const n = new Set(prev); n.delete(id); return n; });
      setExpanded(prev => { const n = new Set(prev); n.delete(id); return n; });
    } catch (e) {
      setError(e.message);
    }
  }

  // Group by memory_type, preserving API order within each group
  const grouped = {};
  for (const e of (entries || [])) {
    if (!grouped[e.memory_type]) grouped[e.memory_type] = [];
    grouped[e.memory_type].push(e);
  }
  const total = entries?.length ?? 0;

  return (
    <div className="systembrain-live">
      {/* Filter / toolbar */}
      <div className="wmem-toolbar">
        <span className="wmem-toolbar-title">Workflow Memory</span>
        {entries !== null && (
          <span className="wmem-total">{total} {total === 1 ? 'entry' : 'entries'}</span>
        )}
        <select
          className="wmem-filter-select"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
        >
          <option value="all">All types</option>
          {ALL_MEMORY_TYPES.map(t => (
            <option key={t} value={t}>{MEMORY_TYPE_LABELS[t] || t}</option>
          ))}
        </select>
        <select
          className="wmem-filter-select"
          value={scopeFilter}
          onChange={e => setScopeFilter(e.target.value)}
        >
          <option value="all">All scopes</option>
          {ALL_SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className="wmem-refresh-btn" onClick={fetchMemory} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <p className="systembrain-error">{error}</p>}
      {entries === null && !error && <p className="systembrain-empty">Loading…</p>}

      {entries !== null && total === 0 && (
        <div className="wmem-empty">
          <p className="wmem-empty-title">No memory entries yet.</p>
          <p className="wmem-empty-hint">
            Memory is written automatically after successful repair retries
            (<code>repair_strategy</code>) and completed plan/build workflow
            runs (<code>planning_pattern</code>).
          </p>
        </div>
      )}

      {entries !== null && total > 0 && (
        <div className="wmem-groups">
          {Object.entries(grouped).map(([type, typeEntries]) => (
            <div key={type} className="wmem-group">
              <div className="wmem-group-header">
                <span className="wmem-group-label">{MEMORY_TYPE_LABELS[type] || type}</span>
                <span className="wmem-group-count">{typeEntries.length}</span>
                <code className="wmem-group-type">{type}</code>
              </div>

              <div className="wmem-cards">
                {typeEntries.map(entry => {
                  const { summary, full } = parseMemoryValue(entry.value);
                  const tags        = Array.isArray(entry.tags) ? entry.tags : [];
                  const isOpen      = expanded.has(entry.id);
                  const isConfirming = confirming.has(entry.id);
                  const conf        = entry.confidence || 'low';

                  return (
                    <div key={entry.id} className={`wmem-card wmem-card--${conf}`}>
                      <div className="wmem-card-header">
                        <span className="wmem-card-key">{entry.key}</span>
                        <span className={`wmem-conf-badge wmem-conf-badge--${conf}`}>{conf}</span>
                        <span className="wmem-card-scope">
                          {entry.scope}{entry.project_id ? ` · p${entry.project_id}` : ''}
                        </span>
                      </div>

                      {summary && (
                        <div className="wmem-card-lesson">{summary}</div>
                      )}

                      {tags.length > 0 && (
                        <div className="wmem-tags">
                          {tags.map((t, i) => <span key={i} className="wmem-tag">{t}</span>)}
                        </div>
                      )}

                      <div className="wmem-card-footer">
                        <span className="wmem-card-source">source: {entry.source}</span>
                        <span className="wmem-card-date">{fmtDate(entry.updated_at)}</span>
                        <button className="wmem-expand-btn" onClick={() => toggleExpanded(entry.id)}>
                          {isOpen ? 'Less' : 'Raw'}
                        </button>
                        {isConfirming ? (
                          <>
                            <button className="wmem-confirm-btn" onClick={() => confirmDelete(entry.id)}>Confirm</button>
                            <button className="wmem-cancel-btn"  onClick={() => cancelDelete(entry.id)}>Cancel</button>
                          </>
                        ) : (
                          <button className="wmem-delete-btn" onClick={() => requestDelete(entry.id)}>Delete</button>
                        )}
                      </div>

                      {isOpen && (
                        <pre className="wmem-raw">{full}</pre>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export default function SystemBrainPanel({ onClose }) {
  const [data, setData]                 = useState(null);
  const [summary, setSummary]           = useState(null);
  const [graphData, setGraphData]       = useState(null);
  const [liveSnap, setLiveSnap]         = useState(null);
  const [liveSummary, setLiveSummary]   = useState(null);
  const [verifications, setVerifications] = useState(null);
  const [verifyRunning, setVerifyRunning] = useState(false);
  const [workflowMeta, setWorkflowMeta] = useState(null);
  const [view, setView]                 = useState('keys');
  const [editing, setEditing]           = useState(null);
  const [editValue, setEditValue]       = useState('');
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [keysRes, sumRes, graphRes, liveRes, liveSumRes, verRes, wfRes] = await Promise.all([
        apiFetch('/api/system-brain').then(r => r.json()),
        apiFetch('/api/system-brain/summary').then(r => r.json()),
        apiFetch('/api/system-brain/capability-graph').then(r => r.json()),
        apiFetch('/api/live-state').then(r => r.json()),
        apiFetch('/api/live-state/summary').then(r => r.json()),
        apiFetch('/api/capability-verifications').then(r => r.json()),
        apiFetch('/api/workflow-meta').then(r => r.json()),
      ]);
      setData(keysRes);
      setSummary(sumRes.summary);
      setGraphData(graphRes);
      setLiveSnap(liveRes);
      setLiveSummary(liveSumRes.summary);
      setVerifications(verRes.verifications || []);
      setWorkflowMeta(wfRes);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  async function runVerifications(capability) {
    setVerifyRunning(capability || 'all');
    try {
      const url = capability
        ? `/api/capability-verifications/run/${capability}`
        : '/api/capability-verifications/run';
      const res  = await apiFetch(url, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      // Reload verification results
      const verRes = await apiFetch('/api/capability-verifications').then(r => r.json());
      setVerifications(verRes.verifications || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setVerifyRunning(false);
    }
  }

  useEffect(() => { load(); }, [load]);

  async function saveEdit() {
    setSaving(true);
    setError(null);
    try {
      let parsed;
      try { parsed = JSON.parse(editValue); } catch (_) { parsed = editValue; }
      const res = await apiFetch(`/api/system-brain/${editing}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: parsed }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${res.status}`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="systembrain-overlay" role="dialog" aria-label="System Brain">
      <div className="systembrain-panel">
        <div className="systembrain-header">
          <span className="systembrain-title">🧠 System Brain</span>
          <div className="systembrain-tabs">
            <button
              className={`systembrain-tab${view === 'keys' ? ' systembrain-tab--active' : ''}`}
              onClick={() => setView('keys')}
            >Keys</button>
            <button
              className={`systembrain-tab${view === 'graph' ? ' systembrain-tab--active' : ''}`}
              onClick={() => setView('graph')}
            >Capability Graph</button>
            <button
              className={`systembrain-tab${view === 'live' ? ' systembrain-tab--active' : ''}`}
              onClick={() => setView('live')}
            >Live State</button>
            <button
              className={`systembrain-tab${view === 'goal' ? ' systembrain-tab--active' : ''}`}
              onClick={() => setView('goal')}
            >Goal Check</button>
            <button
              className={`systembrain-tab${view === 'verify' ? ' systembrain-tab--active' : ''}`}
              onClick={() => setView('verify')}
            >Verifications</button>
            <button
              className={`systembrain-tab${view === 'summary' ? ' systembrain-tab--active' : ''}`}
              onClick={() => setView('summary')}
            >Summary</button>
            <button
              className={`systembrain-tab${view === 'workflow' ? ' systembrain-tab--active' : ''}`}
              onClick={() => setView('workflow')}
            >Workflow Architecture</button>
            <button
              className={`systembrain-tab${view === 'memory' ? ' systembrain-tab--active' : ''}`}
              onClick={() => setView('memory')}
            >Memory</button>
          </div>
          <button className="systembrain-close" onClick={onClose} title="Close">✕</button>
        </div>

        {error && <p className="systembrain-error">{error}</p>}

        {/* ── Live State tab ── */}
        {view === 'live' && (
          <div className="systembrain-live">
            {!liveSnap ? (
              <p className="systembrain-empty">Loading…</p>
            ) : (
              <>
                {/* Overall status badge */}
                <div className={`livestate-overall livestate-overall--${liveSnap.overall}`}>
                  <span className="livestate-overall-dot" />
                  <span className="livestate-overall-label">Overall: {liveSnap.overall.toUpperCase()}</span>
                  <button className="livestate-refresh-btn" onClick={load}>Refresh</button>
                </div>

                {/* Adapters */}
                <div className="livestate-section">
                  <div className="livestate-section-title">Adapters</div>
                  {Object.entries(liveSnap.adapters || {}).map(([name, info]) => {
                    const ok = info.status === 'available' || info.status === 'auth_verified';
                    const label = info.status.replace(/_/g, ' ');
                    return (
                      <div key={name} className={`livestate-row livestate-row--${ok ? 'ok' : 'warn'}`}>
                        <span className="livestate-dot" />
                        <span className="livestate-name">{name}</span>
                        <span className="livestate-status">{label}</span>
                        {info.siteUrl && <span className="livestate-meta">{info.siteUrl}</span>}
                      </div>
                    );
                  })}
                </div>

                {/* Capabilities */}
                <div className="livestate-section">
                  <div className="livestate-section-title">Capabilities</div>
                  {Object.entries(liveSnap.capabilities || {}).map(([cap, info]) => (
                    <div key={cap} className={`livestate-row livestate-row--${info.status === 'available' ? 'ok' : 'blocked'}`}>
                      <span className="livestate-dot" />
                      <span className="livestate-name">{cap}</span>
                      <span className="livestate-status">{info.status}</span>
                      {info.missing?.length > 0 && (
                        <span className="livestate-missing">missing: {info.missing.join(', ')}</span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Execution counters */}
                <div className="livestate-section">
                  <div className="livestate-section-title">Execution</div>
                  {Object.entries(liveSnap.execution || {}).map(([k, v]) => (
                    <div key={k} className="livestate-counter">
                      <span className="livestate-counter-label">{k.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
                      <span className={`livestate-counter-val${v > 0 ? ' livestate-counter-val--nonzero' : ''}`}>{v}</span>
                    </div>
                  ))}
                </div>

                {/* Raw summary */}
                {liveSummary && (
                  <div className="livestate-section">
                    <div className="livestate-section-title">Text Summary</div>
                    <pre className="livestate-summary-text">{liveSummary}</pre>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Goal Check tab ── */}
        {view === 'goal' && (
          <div className="systembrain-live">
            <GoalCheckPanel />
          </div>
        )}

        {/* ── Verifications tab ── */}
        {view === 'verify' && (
          <div className="systembrain-live">
            {/* Header with Run All */}
            <div className="verify-header">
              <span className="verify-header-title">Capability Verifications</span>
              <span className="verify-header-hint">Live checks — not just env var presence</span>
              <button
                className="verify-run-all-btn"
                onClick={() => runVerifications(null)}
                disabled={!!verifyRunning}
              >{verifyRunning === 'all' ? 'Running…' : 'Run All'}</button>
            </div>

            {!verifications ? (
              <p className="systembrain-empty">Loading…</p>
            ) : verifications.length === 0 ? (
              <div className="verify-empty">
                <p>No verifications recorded yet.</p>
                <button className="verify-run-all-btn" onClick={() => runVerifications(null)}>
                  Run All Verifications
                </button>
              </div>
            ) : (
              <div className="verify-list">
                {verifications.map(v => {
                  const isRunning = verifyRunning === v.capability;
                  const statusClass =
                    v.status === 'verified'  ? 'ok'      :
                    v.status === 'degraded'  ? 'warn'    :
                    v.status === 'failed'    ? 'blocked' :
                    v.status === 'skipped'   ? 'skipped' : 'unknown';

                  return (
                    <div key={v.capability} className={`verify-card verify-card--${statusClass}`}>
                      <div className="verify-card-header">
                        <span className="verify-dot" />
                        <span className="verify-capability">{v.capability}</span>
                        <span className={`verify-badge verify-badge--${statusClass}`}>
                          {v.status}
                        </span>
                        {v.age && <span className="verify-age">{v.age}</span>}
                        <button
                          className="verify-run-btn"
                          onClick={() => runVerifications(v.capability)}
                          disabled={!!verifyRunning}
                        >{isRunning ? '…' : 'Run'}</button>
                      </div>

                      {v.error && (
                        <div className="verify-error">{v.error}</div>
                      )}

                      {v.details && Object.keys(v.details).length > 0 && (
                        <div className="verify-details">
                          {Object.entries(v.details).map(([k, val]) => (
                            <span key={k} className="verify-detail-item">
                              <span className="verify-detail-key">{k}</span>
                              <span className="verify-detail-val">
                                {typeof val === 'boolean' ? (val ? 'yes' : 'no') : String(val)}
                              </span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Summary tab ── */}
        {view === 'summary' && (
          <div className="systembrain-summary">
            {summary
              ? <pre className="systembrain-summary-text">{summary}</pre>
              : <p className="systembrain-empty">Loading…</p>
            }
          </div>
        )}

        {/* ── Keys tab ── */}
        {view === 'keys' && (
          <div className="systembrain-keys">
            {!data ? (
              <p className="systembrain-empty">Loading…</p>
            ) : Object.keys(data).length === 0 ? (
              <p className="systembrain-empty">No entries found.</p>
            ) : (
              Object.entries(data).map(([key, value]) => (
                <div key={key} className="systembrain-entry">
                  <div className="systembrain-entry-header">
                    <span className="systembrain-entry-key">{key}</span>
                    {editing !== key && (
                      <button
                        className="systembrain-edit-btn"
                        onClick={() => {
                          setEditing(key);
                          setEditValue(JSON.stringify(value, null, 2));
                        }}
                      >Edit</button>
                    )}
                  </div>
                  {editing === key ? (
                    <div className="systembrain-editor">
                      <textarea
                        className="systembrain-textarea"
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        rows={8}
                      />
                      <div className="systembrain-editor-actions">
                        <button
                          className="systembrain-save-btn"
                          onClick={saveEdit}
                          disabled={saving}
                        >{saving ? 'Saving…' : 'Save'}</button>
                        <button
                          className="systembrain-cancel-btn"
                          onClick={() => setEditing(null)}
                        >Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <pre className="systembrain-value">
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* ── Workflow Architecture tab ── */}
        {view === 'workflow' && (
          <div className="systembrain-live">
            {!workflowMeta ? (
              <p className="systembrain-empty">Loading…</p>
            ) : (
              <>
                <div className="wfarch-intro">
                  Each AI-assisted task passes through a sequence of reasoning steps. This panel shows which AI handles each step, what model is assigned, and how AIs collaborate.
                </div>

                {/* ── Routing table ── */}
                <div className="wfarch-section">
                  <div className="wfarch-section-title">Step-by-Step AI Routing</div>
                  <div className="wfarch-table">
                    <div className="wfarch-thead">
                      <span>Workflow Stage</span>
                      <span>What the System Does</span>
                      <span>AI Worker</span>
                      <span>Model</span>
                      <span>Reasoning Style</span>
                    </div>
                    {(workflowMeta.routing || []).map(row => {
                      const friendlyStage   = PRIMITIVE_FRIENDLY_LABEL[row.primitiveId] || row.primitiveLabel;
                      const primitive       = (workflowMeta.primitives || []).find(p => p.id === row.primitiveId);
                      const modelLabel      = MODEL_FRIENDLY[row.defaultModel] || row.defaultModel || '—';
                      const reasoningLabel  = REASONING_FRIENDLY_LABEL[row.reasoningModeId] || row.reasoningModeLabel || '—';
                      const modeActive      = row.modeActive;
                      return (
                        <div key={row.primitiveId} className="wfarch-row">
                          <div className="wfarch-cell">
                            <span className="wfarch-stage-label">{friendlyStage}</span>
                            <span className="wfarch-raw-id">{row.primitiveId}</span>
                          </div>
                          <div className="wfarch-cell wfarch-cell--desc">
                            {primitive?.description || '—'}
                          </div>
                          <div className="wfarch-cell">
                            <span className="wfarch-worker-label">{row.workerTypeLabel || row.workerType || '—'}</span>
                            <span className="wfarch-raw-id">{row.workerType}</span>
                          </div>
                          <div className="wfarch-cell wfarch-cell--model">
                            {modelLabel}
                          </div>
                          <div className="wfarch-cell">
                            <span className="wfarch-reasoning-label">{reasoningLabel}</span>
                            <span className="wfarch-raw-id">{row.reasoningModeId}</span>
                            {row.reasoningModeId && (
                              <span className={`wfarch-mode-status ${modeActive ? 'wfarch-mode-status--active' : 'wfarch-mode-status--meta'}`}>
                                {modeActive ? 'Live' : 'Metadata only'}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="wfarch-mode-legend">
                    <span className="wfarch-mode-status wfarch-mode-status--active">Live</span> — reasoning mode actively changes how prompts are built and model calls are dispatched.
                    {' '}<span className="wfarch-mode-status wfarch-mode-status--meta">Metadata only</span> — mode is assigned in the registry but not yet operationalized.
                  </div>
                </div>

                {/* ── Stage-level overrides ── */}
                {(workflowMeta.stages || []).some(s => s.reasoningModeSource === 'stage_override') && (
                  <div className="wfarch-section">
                    <div className="wfarch-section-title">Stage-Level Reasoning Exceptions</div>
                    <div className="wfarch-override-note">
                      These stages share a primitive with another stage but execute a different reasoning mode.
                      The effective mode shown here takes precedence over the primitive default.
                    </div>
                    <div className="wfarch-override-list">
                      {(workflowMeta.stages || [])
                        .filter(s => s.reasoningModeSource === 'stage_override')
                        .map(s => {
                          const reasoningLabel = REASONING_FRIENDLY_LABEL[s.reasoningModeId] || s.reasoningModeLabel || s.reasoningModeId;
                          const isActive       = s.modeActive;
                          return (
                            <div key={s.stage} className="wfarch-override-row">
                              <span className="wfarch-override-stage">{s.stage}</span>
                              <span className="wfarch-override-arrow">→</span>
                              <span className="wfarch-override-primitive">{PRIMITIVE_FRIENDLY_LABEL[s.primitiveId] || s.primitiveId}</span>
                              <span className="wfarch-override-arrow">+</span>
                              <span className="wfarch-override-mode">{reasoningLabel}</span>
                              <span className="wfarch-raw-id">{s.reasoningModeId}</span>
                              {s.reasoningModeId && (
                                <span className={`wfarch-mode-status ${isActive ? 'wfarch-mode-status--active' : 'wfarch-mode-status--meta'}`}>
                                  {isActive ? 'Live' : 'Metadata only'}
                                </span>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* ── On-demand / service-level AI operations ── */}
                {(workflowMeta.auxiliaryOps || []).length > 0 && (
                  <div className="wfarch-section">
                    <div className="wfarch-section-title">On-Demand AI Operations</div>
                    <div className="wfarch-override-note">
                      These AI operations are triggered on demand outside the normal workflow sequence.
                      They use the same primitive and reasoning-mode infrastructure as workflow stages
                      but are invoked at the service layer, not the workflow engine.
                    </div>
                    <div className="wfarch-table">
                      <div className="wfarch-thead">
                        <span>Operation</span>
                        <span>What It Does</span>
                        <span>AI Worker</span>
                        <span>Model</span>
                        <span>Reasoning Style</span>
                      </div>
                      {(workflowMeta.auxiliaryOps || []).map(op => {
                        const modelLabel     = MODEL_FRIENDLY[op.defaultModel] || op.defaultModel || '—';
                        const reasoningLabel = REASONING_FRIENDLY_LABEL[op.reasoningModeId] || op.reasoningModeLabel || '—';
                        return (
                          <div key={op.operationId} className="wfarch-row wfarch-row--service">
                            <div className="wfarch-cell">
                              <span className="wfarch-stage-label">{op.label}</span>
                              <span className="wfarch-raw-id">{op.operationId}</span>
                            </div>
                            <div className="wfarch-cell wfarch-cell--desc">
                              {op.description}
                            </div>
                            <div className="wfarch-cell">
                              <span className="wfarch-worker-label">{op.workerTypeLabel || op.workerType || '—'}</span>
                              <span className="wfarch-raw-id">{op.workerType}</span>
                            </div>
                            <div className="wfarch-cell wfarch-cell--model">
                              {modelLabel}
                              <span className="wfarch-auxop-all-models" title="All three models participate in parallel deliberation">+ all models</span>
                            </div>
                            <div className="wfarch-cell">
                              <span className="wfarch-reasoning-label">{reasoningLabel}</span>
                              <span className="wfarch-raw-id">{op.reasoningModeId}</span>
                              {op.reasoningModeId && (
                                <span className={`wfarch-mode-status ${op.modeActive ? 'wfarch-mode-status--active' : 'wfarch-mode-status--meta'}`}>
                                  {op.modeActive ? 'Live' : 'Metadata only'}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Worker → Model mapping ── */}
                <div className="wfarch-section">
                  <div className="wfarch-section-title">AI Worker Models</div>
                  <div className="wfarch-model-grid">
                    {Object.entries(workflowMeta.mappings?.defaultModelForWorker || {}).map(([worker, model]) => {
                      const wt = (workflowMeta.workerTypes || []).find(w => w.id === worker);
                      return (
                        <div key={worker} className="wfarch-model-card">
                          <span className="wfarch-model-worker">{wt?.label || worker}</span>
                          <span className="wfarch-model-arrow">→</span>
                          <span className="wfarch-model-name">{MODEL_FRIENDLY[model] || model}</span>
                          <span className="wfarch-raw-id">{worker}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* ── Lifecycle states ── */}
                <div className="wfarch-section">
                  <div className="wfarch-section-title">Job Lifecycle States</div>
                  {['intake', 'approval', 'execution', 'verification', 'repair', 'terminal'].map(cat => {
                    const states = (workflowMeta.lifecycle?.states || []).filter(s => s.category === cat);
                    if (!states.length) return null;
                    return (
                      <div key={cat} className="wfarch-lifecycle-group">
                        <div className="wfarch-lifecycle-cat">{cat}</div>
                        <div className="wfarch-lifecycle-states">
                          {states.map(s => {
                            const nextIds = workflowMeta.lifecycle?.transitions[s.id] || [];
                            return (
                              <div key={s.id} className={`wfarch-state-card${s.terminal ? ' wfarch-state-card--terminal' : ''}`}>
                                <div className="wfarch-state-label">{s.label}</div>
                                <div className="wfarch-state-desc">{s.description}</div>
                                {nextIds.length > 0 && (
                                  <div className="wfarch-state-next">
                                    <span className="wfarch-state-next-arrow">→</span>
                                    {nextIds.map(next => {
                                      const ns = (workflowMeta.lifecycle.states || []).find(x => x.id === next);
                                      return (
                                        <span key={next} className="wfarch-state-next-item">{ns?.label || next}</span>
                                      );
                                    })}
                                  </div>
                                )}
                                <span className="wfarch-raw-id">{s.id}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Memory tab ── */}
        {view === 'memory' && <WorkflowMemoryPanel />}

        {/* ── Capability Graph tab ── */}
        {view === 'graph' && (
          <div className="systembrain-keys">
            {!graphData ? (
              <p className="systembrain-empty">Loading…</p>
            ) : (
              <>
                {/* Derived facts summary */}
                <div className="capgraph-facts">
                  <div className="capgraph-facts-title">Current System Facts</div>
                  <div className="capgraph-facts-grid">
                    {Object.entries(graphData.facts || {}).map(([k, v]) => (
                      <div key={k} className={`capgraph-fact capgraph-fact--${v ? 'true' : 'false'}`}>
                        <span className="capgraph-fact-dot" />
                        <span className="capgraph-fact-name">{k}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Graph nodes */}
                <div className="capgraph-nodes">
                  {Object.entries(graphData.graph || {}).map(([name, node]) => (
                    <GraphNode key={name} name={name} node={node} />
                  ))}
                </div>

                {/* Capability check form */}
                <CapabilityCheckForm />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
